import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import { FileText, Check, ChevronRight, ChevronLeft, X, ZoomIn, AlertCircle, Trash2 } from 'lucide-react';
import { useBlueCart } from '../context/BlueCartContext';
import { getProductById } from '../services/api';
import DEMO_PRODUCTS from '../data/demoProducts';

// ── LVP Constants ─────────────────────────────────────────────────────────────
const PLANK_DIMENSIONS = ['48inx7in', '48inx9in', '60inx9in'];

// Plank Thickness (mm): 3.5 to 8.0 in 0.5 increments
const PLANK_THICKNESS_OPTIONS = Array.from({ length: 10 }, (_, i) => parseFloat((3.5 + i * 0.5).toFixed(1)));

// Wear Layer (mm): 0.2 to 0.7 in 0.1 increments
const WEAR_LAYER_OPTIONS = Array.from({ length: 6 }, (_, i) => parseFloat((0.2 + i * 0.1).toFixed(1)));

// IXPE Layer (mm): 0.5 to 1.5 in 0.5 increments
const IXPE_OPTIONS = [0.5, 1.0, 1.5];

// Quantity ranges
const QTY_LOWER_RANGES = Array.from({ length: 8 }, (_, i) => ({ label: `${(25 + i * 10).toLocaleString()}k sq ft`, value: `${25 + i * 10}k` })); // 25k–95k in 10k
const QTY_UPPER_RANGES = Array.from({ length: 10 }, (_, i) => ({ label: `${((i + 1) * 100).toLocaleString()}k sq ft`, value: `${(i + 1) * 100}k` })); // 100k–1M in 100k
const QTY_OPTIONS = [
  ...QTY_LOWER_RANGES,
  ...QTY_UPPER_RANGES,
  { label: '1M+ sq ft', value: '1M+' },
];

const MOQ_MAP = { 5.0: 30000, 5.5: 30000, 6.0: 25000, 6.5: 25000 };

function getTotalMm(thickness, ixpe) {
  if (thickness == null || ixpe == null) return null;
  return parseFloat((thickness + ixpe).toFixed(2));
}
function getMOQ(total) { return MOQ_MAP[total] || null; }
function isValidSNum(v) { return /^[Ss]?\d{1,6}$/.test(v.trim()); }

const SPEC_LABELS = {
  thickness:'Thickness (mm)', wear_layer:'Wear Layer', waterproof:'Waterproof',
  surface_texture:'Surface Texture', locking_system:'Locking System', fire_rating:'Fire Rating',
  formaldehyde_free:'Formaldehyde Free', warranty_residential:'Residential Warranty',
  warranty_commercial:'Commercial Warranty', iic_rating:'IIC Rating (dB)', stc_rating:'STC Rating (dB)',
};

/* ─── Lightbox ───────────────────────────────────────────────────────────── */
function Lightbox({ images, initialIndex, productName, onClose }) {
  const [current, setCurrent] = useState(initialIndex);
  const prev = useCallback(() => setCurrent(c => c === 0 ? images.length - 1 : c - 1), [images.length]);
  const next = useCallback(() => setCurrent(c => c === images.length - 1 ? 0 : c + 1), [images.length]);
  useEffect(() => {
    const h = e => { if (e.key==='Escape') onClose(); if (e.key==='ArrowLeft') prev(); if (e.key==='ArrowRight') next(); };
    window.addEventListener('keydown', h); document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [onClose, prev, next]);
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:9999,
        background:'rgba(22,21,18,.92)',
        backdropFilter:'blur(12px)',
        display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
      }}>
      {/* Top bar */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:64, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', zIndex:10 }}>
        <span style={{ fontFamily:'var(--serif)', fontSize:13, letterSpacing:'.08em', color:'rgba(255,255,255,.25)', userSelect:'none', pointerEvents:'none' }}>Contractor Rob Flooring</span>
        <span style={{ fontFamily:'var(--sans)', fontSize:11, letterSpacing:'.2em', textTransform:'uppercase', color:'rgba(255,255,255,.5)' }}>{current+1} / {images.length}</span>
        <button onClick={onClose} aria-label="Close" style={{ width:44, height:44, borderRadius:'50%', background:'rgba(255,255,255,.12)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer', flexShrink:0 }}><X size={20}/></button>
      </div>
      {/* Prev */}
      {images.length>1&&<button onClick={e=>{e.stopPropagation();prev();}} aria-label="Previous" style={{ position:'absolute', left:16, top:'50%', transform:'translateY(-50%)', zIndex:10, width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,.12)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><ChevronLeft size={26}/></button>}
      {/* Image — fills remaining space with safe padding for the top bar and thumbnail strip */}
      <div
        onClick={e=>e.stopPropagation()}
        style={{
          width:'100%', height:'100%',
          paddingTop:72, paddingBottom: images.length>1 ? 88 : 24, paddingLeft:72, paddingRight:72,
          display:'flex', alignItems:'center', justifyContent:'center',
          boxSizing:'border-box',
        }}>
        <img
          key={current}
          src={images[current]}
          alt={`${productName} ${current+1}`}
          style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', borderRadius:'var(--r-sm)', boxShadow:'0 32px 80px rgba(0,0,0,.5)' }}
        />
      </div>
      {/* Next */}
      {images.length>1&&<button onClick={e=>{e.stopPropagation();next();}} aria-label="Next" style={{ position:'absolute', right:16, top:'50%', transform:'translateY(-50%)', zIndex:10, width:48, height:48, borderRadius:'50%', background:'rgba(255,255,255,.12)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><ChevronRight size={26}/></button>}
      {/* Thumbnail strip */}
      {images.length>1&&(
        <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:16, left:'50%', transform:'translateX(-50%)', display:'flex', gap:8, background:'rgba(22,21,18,.6)', backdropFilter:'blur(8px)', padding:8, borderRadius:'var(--r-md)', maxWidth:'80vw', overflowX:'auto', scrollbarWidth:'none' }}>
          {images.map((img,idx)=><button key={idx} onClick={()=>setCurrent(idx)} style={{ flexShrink:0, width:56, height:42, overflow:'hidden', borderRadius:'var(--r-xs)', border:`2px solid ${idx===current?'var(--saffron)':'transparent'}`, opacity:idx===current?1:0.5, cursor:'pointer', padding:0, background:'var(--linen)' }}><img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/></button>)}
        </div>
      )}
    </div>,
    document.body
  );
}

/* ─── Gallery ────────────────────────────────────────────────────────────── */
function ProductGallery({ images, productName }) {
  const [active, setActive] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const stripRef = useRef(null);
  useEffect(() => {
    const strip = stripRef.current; if (!strip) return;
    const thumb = strip.children[active]; if (thumb) thumb.scrollIntoView({ behavior:'smooth', block:'nearest', inline:'center' });
  }, [active]);
  return (
    <>
      <div onClick={()=>setLightboxOpen(true)} style={{ aspectRatio:'4/3', overflow:'hidden', borderRadius:'var(--r-md)', background:'var(--linen)', cursor:'zoom-in', position:'relative' }}>
        <img key={active} src={images[active]} alt={`${productName} – ${active+1}`} style={{ width:'100%', height:'100%', objectFit:'cover', transition:'transform .6s var(--ease-out)' }} onMouseEnter={e=>e.currentTarget.style.transform='scale(1.04)'} onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}/>
        <div style={{ position:'absolute', top:12, left:12, background:'rgba(22,21,18,.55)', backdropFilter:'blur(6px)', color:'#fff', display:'flex', alignItems:'center', gap:6, fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.18em', textTransform:'uppercase', padding:'6px 12px', borderRadius:'var(--r-sm)', pointerEvents:'none' }}><ZoomIn size={12}/>{active+1} / {images.length}</div>
      </div>
      {images.length>1&&(
        <div ref={stripRef} className="product-gallery-strip" style={{ display:'flex', gap:8, marginTop:10, overflowX:'auto', paddingBottom:4, scrollbarWidth:'thin', scrollbarColor:'var(--mist) transparent' }}>
          {images.map((img,i)=><button key={i} onClick={()=>setActive(i)} style={{ flexShrink:0, width:72, height:54, overflow:'hidden', borderRadius:'var(--r-xs)', border:`2px solid ${i===active?'var(--saffron)':'transparent'}`, opacity:i===active?1:0.5, cursor:'pointer', background:'var(--linen)', padding:0, transition:'all .25s' }} onMouseEnter={e=>{if(i!==active)e.currentTarget.style.opacity='.85';}} onMouseLeave={e=>{if(i!==active)e.currentTarget.style.opacity='.5';}}><img src={img} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/></button>)}
        </div>
      )}
      {lightboxOpen&&<Lightbox images={images} productName={productName} initialIndex={active} onClose={()=>setLightboxOpen(false)}/>}
    </>
  );
}

/* ─── LVP Configurator ───────────────────────────────────────────────────── */
function LVPConfigurator({ config, onChange, errors }) {
  const totalMm = getTotalMm(config.plank_thickness_mm, config.ixpe_layer_mm);
  const moq = getMOQ(totalMm);

  const sel = (hasErr) => ({
    width:'100%', padding:'10px 12px', fontFamily:'var(--sans)', fontSize:14, color:'var(--ink)',
    background:'var(--bone)', border:`1px solid ${hasErr?'#C84040':'var(--rule)'}`, borderRadius:'var(--r-sm)',
    outline:'none', cursor:'pointer', appearance:'none',
    backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236E6A60' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center', paddingRight:36,
  });
  const inp = (hasErr) => ({ width:'100%', padding:'10px 12px', fontFamily:'var(--mono)', fontSize:14, color:'var(--ink)', background:'var(--bone)', border:`1px solid ${hasErr?'#C84040':'var(--rule)'}`, borderRadius:'var(--r-sm)', outline:'none', letterSpacing:'0.05em' });
  const lbl = { display:'block', fontFamily:'var(--sans)', fontSize:10, fontWeight:600, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--ash)', marginBottom:6 };
  const err = { fontFamily:'var(--sans)', fontSize:11, color:'#C84040', marginTop:4, display:'flex', alignItems:'center', gap:4 };

  return (
    <div className="lvp-configurator" style={{ marginTop:24, padding:'20px', background:'var(--ivory-soft)', borderRadius:'var(--r-md)', border:'1px solid var(--rule)' }}>
      <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:16 }}>Configure Your Order</p>
      <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {/* S# */}
        <div>
          <label style={lbl}>Finish Code (S#) <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
          <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:6, lineHeight:1.4 }}>Enter the S# from the product gallery — e.g. S1234 or 1234</p>
          <input type="text" value={config.s_number} maxLength={7} placeholder="S1234"
            onChange={e => { const raw = e.target.value.replace(/[^Ss0-9]/g,'').replace(/^([Ss]?)([Ss]+)/g,'$1').slice(0,7); onChange({ s_number: raw.toUpperCase() }); }}
            style={inp(!!errors?.s_number)} aria-label="S Number finish code"/>
          {errors?.s_number&&<p style={err}><AlertCircle size={11}/>{errors.s_number}</p>}
        </div>
        {/* Plank Dimension */}
        <div>
          <label style={lbl}>Plank Dimension <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
          <select value={config.plank_dimension} onChange={e=>onChange({plank_dimension:e.target.value})} style={sel(!!errors?.plank_dimension)} aria-label="Plank Dimension">
            <option value="">Select plank size</option>
            {PLANK_DIMENSIONS.map(d=><option key={d} value={d}>{d}</option>)}
          </select>
          {errors?.plank_dimension&&<p style={err}><AlertCircle size={11}/>{errors.plank_dimension}</p>}
        </div>
        {/* Plank Thickness */}
        <div>
          <label style={lbl}>Plank Thickness + Wear Layer (mm) <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
          <select value={config.plank_thickness_mm==null?'':String(config.plank_thickness_mm)} onChange={e=>onChange({plank_thickness_mm:e.target.value?parseFloat(e.target.value):null})} style={sel(!!errors?.plank_thickness_mm)} aria-label="Plank Thickness">
            <option value="">Select plank thickness</option>
            {PLANK_THICKNESS_OPTIONS.map(mm=><option key={mm} value={String(mm)}>{mm.toFixed(1)}mm</option>)}
          </select>
          {errors?.plank_thickness_mm&&<p style={err}><AlertCircle size={11}/>{errors.plank_thickness_mm}</p>}
        </div>
        {/* Wear Layer */}
        <div>
          <label style={lbl}>Wear Layer (mm) <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
          <select value={config.wear_layer_mm==null?'':String(config.wear_layer_mm)} onChange={e=>onChange({wear_layer_mm:e.target.value?parseFloat(e.target.value):null})} style={sel(!!errors?.wear_layer_mm)} aria-label="Wear Layer">
            <option value="">Select wear layer</option>
            {WEAR_LAYER_OPTIONS.map(mm=><option key={mm} value={String(mm)}>{mm}mm</option>)}
          </select>
          {errors?.wear_layer_mm&&<p style={err}><AlertCircle size={11}/>{errors.wear_layer_mm}</p>}
        </div>
        {/* IXPE Layer */}
        <div>
          <label style={lbl}>IXPE Underlayment (mm) <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
          <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:6, lineHeight:1.4 }}>Pre-attached IXPE pad — contributes to total thickness</p>
          <select value={config.ixpe_layer_mm==null?'':String(config.ixpe_layer_mm)} onChange={e=>onChange({ixpe_layer_mm:e.target.value?parseFloat(e.target.value):null})} style={sel(!!errors?.ixpe_layer_mm)} aria-label="IXPE Layer">
            <option value="">Select IXPE thickness</option>
            {IXPE_OPTIONS.map(mm=><option key={mm} value={String(mm)}>{mm.toFixed(1)}mm IXPE</option>)}
          </select>
          {errors?.ixpe_layer_mm&&<p style={err}><AlertCircle size={11}/>{errors.ixpe_layer_mm}</p>}
        </div>
        {/* Total Thickness — calculated display */}
        <div style={{ padding:'14px 16px', background:'rgba(200,148,31,.07)', borderRadius:'var(--r-sm)', border:'1px solid rgba(200,148,31,.25)' }}>
          <p style={{ fontFamily:'var(--sans)', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'var(--ash)', marginBottom:6 }}>Total Thickness</p>
          {totalMm!=null ? (
            <div style={{ display:'flex', alignItems:'center', gap:24 }}>
              <p style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)', lineHeight:1 }}>{totalMm}mm</p>
              {moq!=null&&<><div style={{ width:1, height:36, background:'var(--rule)' }}/><div>
                <p style={{ fontFamily:'var(--sans)', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'var(--ash)', marginBottom:3 }}>Min. Order Quantity</p>
                <p style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)', lineHeight:1 }}>{moq.toLocaleString()} <span style={{ fontSize:13, color:'var(--ash)', fontFamily:'var(--sans)' }}>sq ft</span></p>
              </div></>}
            </div>
          ) : (
            <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', fontStyle:'italic' }}>Calculated automatically from selections above</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Added Config Card (inline below configurator) ──────────────────────── */
function ConfigCard({ item, onRemove }) {
  const cfg = item.configuration || {};
  const totalMm = cfg.total_thickness_mm;
  const moq = getMOQ(totalMm);
  return (
    <div style={{ padding:'14px 16px', background:'rgba(59,95,122,.05)', border:'1px solid rgba(59,95,122,.22)', borderRadius:'var(--r-sm)', display:'flex', flexDirection:'column', gap:8 }}>
      {/* Row 1 — S# + dimension + remove */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <Check size={14} style={{ color:'var(--indigo)', flexShrink:0 }}/>
          <span style={{ fontFamily:'var(--mono)', fontSize:13, color:'var(--indigo)', fontWeight:600 }}>S# {cfg.s_number}</span>
          {cfg.plank_dimension&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', background:'var(--rule)', padding:'2px 7px', borderRadius:'var(--r-xs)' }}>{cfg.plank_dimension}</span>}
        </div>
        <button onClick={()=>onRemove(item.cartItemId)} aria-label="Remove" style={{ color:'var(--mist)', background:'none', border:'none', cursor:'pointer', padding:2, flexShrink:0, transition:'color .2s' }} onMouseEnter={e=>e.currentTarget.style.color='#c0392b'} onMouseLeave={e=>e.currentTarget.style.color='var(--mist)'}><Trash2 size={14}/></button>
      </div>
      {/* Row 2 — thickness + wear + IXPE + total */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 12px' }}>
        {cfg.plank_thickness_mm!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)' }}>{cfg.plank_thickness_mm.toFixed(1)}mm plank</span>}
        {cfg.wear_layer_mm!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)' }}>{cfg.wear_layer_mm.toFixed(1)}mm wear</span>}
        {cfg.ixpe_layer_mm!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)' }}>+ {cfg.ixpe_layer_mm.toFixed(1)}mm IXPE</span>}
        {totalMm!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ink)', fontWeight:600 }}>= {totalMm}mm total</span>}
        {moq!=null&&<span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--saffron-deep)' }}>MOQ {moq.toLocaleString()} sq ft</span>}
      </div>
      {/* Row 3 — order quantity badge */}
      {cfg.order_quantity&&(
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', letterSpacing:'.06em' }}>Qty:</span>
          <span style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--indigo)', background:'rgba(59,95,122,.08)', padding:'2px 8px', borderRadius:'var(--r-xs)' }}>{cfg.order_quantity}</span>
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */
export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('specs');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [lvpConfig, setLvpConfig] = useState({ s_number:'', plank_dimension:'', plank_thickness_mm:null, wear_layer_mm:null, ixpe_layer_mm:null });
  const [selectedQty, setSelectedQty] = useState('');
  const [customQty, setCustomQty] = useState('');
  const [configErrors, setConfigErrors] = useState({});

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const h = e => setIsMobile(e.matches);
    mq.addEventListener('change', h); setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', h);
  }, []);

  const { addItem, items, updateQuantity, removeItem } = useBlueCart();
  // All cart items for THIS product
  const productItems = product ? items.filter(i => i.product._id === product._id) : [];
  const hasAny = productItems.length > 0;

  useEffect(() => {
    getProductById(id)
      .then(res => setProduct(res.data))
      .catch(() => { const demo = DEMO_PRODUCTS.find(p => p._id === id) || DEMO_PRODUCTS[0]; setProduct(demo); })
      .finally(() => setLoading(false));
  }, [id]);

  const handleConfigChange = (patch) => {
    setLvpConfig(prev => ({ ...prev, ...patch }));
    const cleared = {}; Object.keys(patch).forEach(k => { cleared[k] = undefined; });
    setConfigErrors(prev => ({ ...prev, ...cleared }));
  };

  const validateConfig = () => {
    const e = {};
    if (!lvpConfig.s_number.trim()) e.s_number = 'Please enter the S# finish code.';
    else if (!isValidSNum(lvpConfig.s_number)) e.s_number = 'S# must be "S" + up to 6 digits (e.g. S1234).';
    if (!lvpConfig.plank_dimension) e.plank_dimension = 'Please select a plank dimension.';
    if (lvpConfig.plank_thickness_mm == null) e.plank_thickness_mm = 'Please select a plank thickness.';
    if (lvpConfig.wear_layer_mm == null) e.wear_layer_mm = 'Please select a wear layer.';
    if (lvpConfig.ixpe_layer_mm == null) e.ixpe_layer_mm = 'Please select an IXPE layer.';
    if (!selectedQty) e.selectedQty = 'Please select an order quantity.';
    if (selectedQty === '1M+' && !customQty.trim()) e.customQty = 'Please describe your quantity requirement.';
    return e;
  };

  const handleAdd = () => {
    const errs = validateConfig();
    if (Object.keys(errs).length > 0) { setConfigErrors(errs); return; }
    const totalMm = getTotalMm(lvpConfig.plank_thickness_mm, lvpConfig.ixpe_layer_mm);
    const qtyLabel = selectedQty === '1M+' ? customQty.trim() : selectedQty;
    addItem(product, 1, { ...lvpConfig, total_thickness_mm: totalMm, order_quantity: qtyLabel });
    // Reset for next config
    setLvpConfig({ s_number:'', plank_dimension:'', plank_thickness_mm:null, wear_layer_mm:null, ixpe_layer_mm:null });
    setSelectedQty('');
    setCustomQty('');
    setConfigErrors({});
  };

  if (loading) return (
    <main style={{ paddingTop:76 }} className="page-container">
      <div style={{ padding:'64px 0' }}>
        <div className="skeleton" style={{ aspectRatio:'4/3', borderRadius:'var(--r-md)' }}/>
        <div className="skeleton" style={{ height:32, marginTop:24, width:'35%' }}/>
        <div className="skeleton" style={{ height:16, marginTop:12, width:'60%' }}/>
      </div>
    </main>
  );

  if (!product) return null;

  const images = product.image_urls?.length
    ? product.image_urls
    : [`https://via.placeholder.com/800x600/ECE6D6/6E6A60?text=${encodeURIComponent(product.name||'Product')}`];

  return (
    <main style={{ paddingTop: isMobile ? 64 : 76 }}>
      <div className="page-container" style={{ paddingTop:48, paddingBottom:96 }}>
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2" style={{ fontFamily:'var(--sans)', fontSize:11, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--ash)', marginBottom:32 }}>
          <Link to="/" style={{ color:'var(--ash)' }}>Home</Link><ChevronRight size={11}/>
          <Link to="/catalog" style={{ color:'var(--ash)' }}>Catalog</Link><ChevronRight size={11}/>
          <span style={{ color:'var(--ink)' }}>{product.name}</span>
        </nav>

        <div className="product-detail-grid" style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))', gap: isMobile ? 32 : 56 }}>
          {/* Gallery — .product-gallery-main constrains it on mobile */}
          <div className="animate-fade-up product-gallery-main"><ProductGallery images={images} productName={product.name}/></div>

          {/* Info */}
          <div className="animate-fade-up delay-1">
            {product.category&&<p className="eyebrow" style={{ marginBottom:12 }}>{product.category}</p>}
            <h1 style={{ fontFamily:'var(--serif)', fontSize:'clamp(30px,5.5vw,68px)', lineHeight:1, textWrap:'balance' }}>{product.name}</h1>
            <div style={{ width:32, height:1, background:'var(--saffron)', margin:'20px 0 24px' }}/>
            <p style={{ fontFamily:'var(--sans)', fontSize:16, color:'var(--ink-soft)', lineHeight:1.65 }}>{product.description}</p>

            {/* Pricing banner */}
            <div style={{ marginTop:28, padding:'16px 20px', background:'var(--ivory-soft)', borderRadius:'var(--r-md)', display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--saffron)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><FileText size={16} style={{ color:'var(--ink)' }}/></div>
              <div>
                <p style={{ fontFamily:'var(--sans)', fontSize:11, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--ink)', fontWeight:600 }}>Wholesale Quote-Based Pricing</p>
                <p style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)', marginTop:4 }}>Custom rates by project volume — returned in 1–2 business days.</p>
              </div>
            </div>

            {/* Configurator */}
            <LVPConfigurator config={lvpConfig} onChange={handleConfigChange} errors={configErrors}/>

            {/* Quantity selector */}
            <div style={{ marginTop:16, padding:'16px 20px', background:'var(--ivory-soft)', borderRadius:'var(--r-md)', border:'1px solid var(--rule)' }}>
              <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:12 }}>Select Quantity (sq ft)</p>
              <select
                value={selectedQty}
                onChange={e => { setSelectedQty(e.target.value); if (e.target.value !== '1M+') setCustomQty(''); setConfigErrors(prev=>({...prev, selectedQty:undefined, customQty:undefined})); }}
                style={{
                  width:'100%', padding:'10px 12px', fontFamily:'var(--sans)', fontSize:14, color:'var(--ink)',
                  background:'var(--bone)', border:`1px solid ${configErrors?.selectedQty?'#C84040':'var(--rule)'}`, borderRadius:'var(--r-sm)',
                  outline:'none', cursor:'pointer', appearance:'none',
                  backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236E6A60' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                  backgroundRepeat:'no-repeat', backgroundPosition:'right 12px center', paddingRight:36,
                }}
                aria-label="Order Quantity">
                <option value="">Select order quantity</option>
                <optgroup label="25k – 100k sq ft">
                  {QTY_OPTIONS.filter(o=>o.value.endsWith('k') && parseInt(o.value)>=25 && parseInt(o.value)<=100).map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </optgroup>
                <optgroup label="100k – 1M sq ft">
                  {QTY_OPTIONS.filter(o=>o.value.endsWith('k') && parseInt(o.value)>100).map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </optgroup>
                <optgroup label="Large Volume">
                  <option value="1M+">1M+ sq ft</option>
                </optgroup>
              </select>
              {configErrors?.selectedQty&&<p style={{ fontFamily:'var(--sans)', fontSize:11, color:'#C84040', marginTop:4, display:'flex', alignItems:'center', gap:4 }}><AlertCircle size={11}/>{configErrors.selectedQty}</p>}
              {selectedQty==='1M+'&&(
                <div style={{ marginTop:12 }}>
                  <label style={{ display:'block', fontFamily:'var(--sans)', fontSize:10, fontWeight:600, letterSpacing:'.18em', textTransform:'uppercase', color:'var(--ash)', marginBottom:6 }}>Describe your quantity requirement <span style={{ color:'var(--saffron-deep)' }}>*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. 2.5 million sq ft across 3 phases"
                    value={customQty}
                    onChange={e => { setCustomQty(e.target.value); setConfigErrors(prev=>({...prev, customQty:undefined})); }}
                    style={{ width:'100%', padding:'10px 12px', fontFamily:'var(--sans)', fontSize:14, color:'var(--ink)', background:'var(--bone)', border:`1px solid ${configErrors?.customQty?'#C84040':'var(--rule)'}`, borderRadius:'var(--r-sm)', outline:'none' }}
                  />
                  {configErrors?.customQty&&<p style={{ fontFamily:'var(--sans)', fontSize:11, color:'#C84040', marginTop:4, display:'flex', alignItems:'center', gap:4 }}><AlertCircle size={11}/>{configErrors.customQty}</p>}
                </div>
              )}
            </div>
            {/* Add to Selected Products CTA */}
            <div style={{ marginTop:16 }}>
              <button onClick={handleAdd} className="btn btn-primary btn-lg" style={{ width:'100%', justifyContent:'center', borderRadius:'var(--r-sm)' }}>
                <FileText size={15}/> {hasAny ? 'Add Additional Selections' : 'Add to Selected Products'}
              </button>
            </div>
            <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', textAlign:'center', marginTop:10, letterSpacing:'.06em' }}>Free consultation · Wholesale pricing · Fast turnaround</p>

            {/* Added config cards list */}
            {hasAny&&(
              <div style={{ marginTop:24 }}>
                <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.2em', textTransform:'uppercase', color:'var(--indigo)', fontWeight:600, marginBottom:12 }}>
                  Added to Selected Products ({productItems.length} configuration{productItems.length!==1?'s':''})
                </p>
                <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  {productItems.map(item=>(
                    <ConfigCard key={item.cartItemId} item={item} onRemove={removeItem}/>
                  ))}
                </div>
                <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', gap:10, marginTop:16 }}>
                  <Link to="/rfq/submit" className="btn btn-primary btn-lg" style={{ flex:1, justifyContent:'center', borderRadius:'var(--r-sm)' }}>
                    <FileText size={15}/> Request Wholesale Pricing
                  </Link>
                  <Link to="/catalog" className="btn btn-outline" style={{ flex:1, justifyContent:'center', borderRadius:'var(--r-sm)', textDecoration:'none', display:'flex', alignItems:'center', gap:8 }}>
                    Continue Browsing
                  </Link>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div style={{ marginTop:40 }}>
              <div className="flex" style={{ borderBottom:'1px solid var(--rule)', overflowX: isMobile ? 'auto' : 'visible', scrollbarWidth:'none' }}>
                {[{id:'specs',label:'Specifications'},{id:'layers',label:'Construction'},{id:'features',label:'Features'}].map(t=>(
                  <button key={t.id} onClick={()=>setTab(t.id)} className="eyebrow" style={{ padding:'14px 20px', position:'relative', color:tab===t.id?'var(--ink)':'var(--ash)', cursor:'pointer', background:'none', border:'none', transition:'color .2s' }}>
                    {t.label}{tab===t.id&&<span style={{ position:'absolute', left:0, right:0, bottom:-1, height:2, background:'var(--saffron)' }}/>}
                  </button>
                ))}
              </div>
              <div style={{ marginTop:24 }} key={tab}>
                {tab==='specs'&&product.specs&&(
                  <dl style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: isMobile ? 0 : '20px 24px' }}>
                    {Object.entries(product.specs).map(([k,v])=>(
                      <div key={k} style={{ paddingBottom:12, paddingTop: isMobile ? 12 : 0, borderBottom:'1px solid var(--rule)' }}>
                        <dt className="eyebrow" style={{ marginBottom:8 }}>{SPEC_LABELS[k]||k.replace(/_/g,' ')}</dt>
                        <dd style={{ fontFamily:'var(--serif)', fontSize:20, color:'var(--ink)' }}>{typeof v==='boolean'?(v?'Yes':'No'):String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {tab==='layers'&&(
                  <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:10 }}>
                    {[['UV Coating','Anti-fade, increased grain clarity, anti-scratch finish'],['Wear Layer','Anti-impact, durable performance over 20+ years of use'],['Printed Décor','Photorealistic patterns based on current architectural trends'],['Rigid SPC Core','CaCO₃ powder + virgin plastic powder composite'],['Underlayment','Pre-attached, withstands -10°C to 45°C, acoustic dampening']].map(([k,v],i)=>(
                      <li key={k} className="flex items-start gap-3" style={{ padding:14, background:'var(--ivory-soft)', borderRadius:'var(--r-sm)' }}>
                        <span style={{ fontFamily:'var(--mono)', color:'var(--saffron-deep)', fontSize:12, marginTop:2, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</span>
                        <div><p style={{ fontFamily:'var(--serif)', fontSize:18 }}>{k}</p><p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', marginTop:3 }}>{v}</p></div>
                      </li>
                    ))}
                  </ul>
                )}
                {tab==='features'&&(
                  <ul style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap:10, listStyle:'none' }}>
                    {['Waterproof & damp-proof','NFPA Class 2 fire retardant','Formaldehyde-free','Scratch & stain resistant','Click-lock installation','Suitable for radiant heat','Residential & light commercial','Pre-attached underlayment'].map(f=>(
                      <li key={f} className="flex items-center gap-2" style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ink-soft)' }}>
                        <Check size={13} style={{ color:'var(--saffron-deep)', flexShrink:0 }}/>{f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
